import { EnvironmentDetailsHeader } from "@/components/environments/EnvironmentDetailsHeader";
import { EnvironmentDeletionWarning } from "@/components/environments/EnvironmentDeletionWarning";
import { EnvironmentOnboarding } from "@/components/environments/EnvironmentOnboarding";
import { EnvironmentPorts } from "@/components/environments/EnvironmentPorts";
import { EnvironmentSecretsModal } from "@/components/environments/EnvironmentSecretsModal";
import { EnvironmentStartSequence } from "@/components/environments/EnvironmentStartSequence";
import { useEnvironmentDetailsOutletContext } from "@/hooks/use-environment-details-outlet-context";
import { useFlagOnaEnvironmentOnboardingEnabled } from "@/hooks/use-flag";
import { TrackLocations } from "@/hooks/use-segment";
import { useEnvironment } from "@/queries/environment-queries";
import { EnvironmentAutomations } from "@/routes/environments/details/EnvironmentAutomations";
import { useState, type FC, type PropsWithChildren } from "react";
import { GitStatusPanel } from "@/routes/environments/details/GitStatusPanel";

export type EnvironmentDetailsProps = {
    environmentId: string;
    /** Override for showingAgentConversation when rendered outside router Outlet */
    showingAgentConversation?: boolean;
};

export const EnvironmentDetails: FC<EnvironmentDetailsProps> = ({
    environmentId,
    showingAgentConversation: showingAgentConversationProp,
}) => {
    const [showSecretsModal, setShowSecretsModal] = useState(false);
    const { value: isOnaEnvironmentOnboardingEnabled } = useFlagOnaEnvironmentOnboardingEnabled();
    const outletContext = useEnvironmentDetailsOutletContext();
    const showingAgentConversation = showingAgentConversationProp ?? outletContext?.showingAgentConversation ?? false;

    return (
        <div className="flex min-h-full flex-col gap-6 @container" data-testid="environment-details">
            {!showingAgentConversation && <EnvironmentDetailsHeader showActionBar={true} />}

            <WhenReady environmentId={environmentId}>
                <>
                    <EnvironmentDeletionWarning environmentId={environmentId} />
                    <EnvironmentStartSequence
                        environmentId={environmentId}
                        onShowSecrets={() => setShowSecretsModal(true)}
                    />
                    {!showingAgentConversation && <GitStatusPanel environmentId={environmentId} />}
                    {!isOnaEnvironmentOnboardingEnabled && (
                        <EnvironmentOnboarding
                            environmentId={environmentId}
                            data-track-location={TrackLocations.EnvironmentOnboarding}
                        />
                    )}
                    <EnvironmentPorts environmentId={environmentId} />
                    <EnvironmentAutomations environmentId={environmentId} />
                </>
            </WhenReady>

            {showSecretsModal && (
                <EnvironmentSecretsModal onClose={() => setShowSecretsModal(false)} environmentId={environmentId} />
            )}
        </div>
    );
};

export const WhenReady: FC<PropsWithChildren & { environmentId: string }> = ({ children, environmentId }) => {
    const { data: environment, isPending } = useEnvironment(environmentId);
    const ready = !isPending && !!environment;

    if (!ready) {
        return null;
    }

    return children;
};
